/**
 * Pair-approval queue — when the auto-matcher can't find a perfect
 * partner for a freshly-confirmed account, it writes rows here instead
 * of either silently dropping the account or silently creating a bad
 * pair. The operator reviews from the dashboard.
 */

import { z } from "zod";

/** Rule fields we compare when deciding pair compatibility. */
export const PAIR_DELTA_FIELDS = [
  "account_size",
  "daily_loss_pct",
  "max_drawdown_pct",
  "daily_dd_type",
  "max_dd_type",
  "phase",
  "challenge_type",
  "min_trading_days",
] as const;
export type PairDeltaField = (typeof PAIR_DELTA_FIELDS)[number];

/**
 * One field's values on both sides + whether they match.
 * `diff` is absolute difference for numerics, null for categoricals.
 */
export const PairDeltaEntryZ = z.object({
  field: z.string(),
  a: z.union([z.string(), z.number(), z.null()]),
  b: z.union([z.string(), z.number(), z.null()]),
  match: z.boolean(),
  diff: z.number().nullable(),
});
export type PairDeltaEntry = z.infer<typeof PairDeltaEntryZ>;

export const PairQualityZ = z.enum(["perfect", "near", "mismatch", "operator_forced"]);
export type PairQuality = z.infer<typeof PairQualityZ>;

export const PairProposalStatusZ = z.enum(["pending", "approved", "rejected", "expired"]);
export type PairProposalStatus = z.infer<typeof PairProposalStatusZ>;

export const PairProposalListItemZ = z.object({
  id: z.string(),
  slug: z.string(),
  candidateA: z.object({
    accountId: z.string(),
    slug: z.string(),
    firmName: z.string(),
    firmLogo: z.string(),
    login: z.string().nullable(),
    phase: z.string(),
    accountSize: z.number().nullable(),
  }),
  candidateB: z.object({
    accountId: z.string(),
    slug: z.string(),
    firmName: z.string(),
    firmLogo: z.string(),
    login: z.string().nullable(),
    phase: z.string(),
    accountSize: z.number().nullable(),
  }),
  deltas: z.array(PairDeltaEntryZ),
  quality: z.enum(["perfect", "near", "mismatch"]),
  status: PairProposalStatusZ,
  createdAt: z.number(),
  decidedAt: z.number().nullable(),
  decidedBy: z.string().nullable(),
  decidedReason: z.string().nullable(),
});
export type PairProposalListItem = z.infer<typeof PairProposalListItemZ>;

export const ListPairProposalsResZ = z.object({
  proposals: z.array(PairProposalListItemZ),
});
export type ListPairProposalsRes = z.infer<typeof ListPairProposalsResZ>;

export const ApprovePairProposalReqZ = z.object({
  reason: z.string().max(500).optional(),
});
export type ApprovePairProposalReq = z.infer<typeof ApprovePairProposalReqZ>;

export const RejectPairProposalReqZ = z.object({
  reason: z.string().max(500).optional(),
});
export type RejectPairProposalReq = z.infer<typeof RejectPairProposalReqZ>;

export const ForcePairReqZ = z.object({
  longAccountId: z.string().min(1).max(32),
  shortAccountId: z.string().min(1).max(32),
  acknowledgedDeltas: z.literal(true),
  reason: z.string().max(500).optional(),
});
export type ForcePairReq = z.infer<typeof ForcePairReqZ>;

export const SwapLegReqZ = z.object({
  side: z.enum(["long", "short"]),
  newAccountId: z.string().min(1).max(32),
  acknowledgedDeltas: z.boolean().default(false),
  reason: z.string().max(500).optional(),
});
export type SwapLegReq = z.infer<typeof SwapLegReqZ>;
